import { TaskType } from './taskTypes';

/**
 * "What matters most for these labels?" — the two-label single-label
 * classification choice made on the Business Goals step and persisted on the
 * usecase as `label_priority: { kind, label } | null`.
 *
 * Shared between the creation wizard (which writes it) and the Train step
 * (which seeds its Advanced defaults from it), so it lives in `shared/domain`.
 * Kinds mirror `LabelPriorityKind` in optune-utils.
 */
export const LABEL_PRIORITY_KIND = Object.freeze({
    equal: 'equal',
    dontMiss: 'dont_miss',
    avoidFalseDetections: 'avoid_false_detections',
});

/** Metric used whenever a priority choice seeds the Train defaults. */
export const LABEL_PRIORITY_METRIC = 'f_beta_score';

const BETA_BY_KIND = Object.freeze({
    [LABEL_PRIORITY_KIND.equal]: 1,
    // Recall-leaning: catch every occurrence of the chosen label.
    [LABEL_PRIORITY_KIND.dontMiss]: 2,
    // Precision-leaning: fewer false detections of the chosen label.
    [LABEL_PRIORITY_KIND.avoidFalseDetections]: 0.5,
});

/**
 * Labels a task's rare-class "Optimize on" control may target: exactly the two
 * expected labels of a single-label classification task, otherwise `[]`
 * (multi-label, other task types, more or fewer labels). Mirrors the trainer's
 * validation of `rare_class_label`, so a label picked here can never be
 * rejected at training time.
 *
 * `manually_added_labels` ("Labels in this task") is the source of truth;
 * legacy tasks created before that field existed fall back to the labels seen
 * in the uploaded data.
 */
export const rareClassEligibleLabels = (usecase) => {
    if (!usecase || usecase.task_type !== TaskType.classification || usecase.is_multilabel) {
        return [];
    }
    const labels = classificationTaskLabels(usecase);
    return labels.length === 2 ? [...labels] : [];
};

/**
 * The labels of a classification task: "Labels in this task"
 * (`manually_added_labels`), falling back to the labels seen in the uploaded
 * data for legacy tasks. `[]` for other task types.
 */
export const classificationTaskLabels = (usecase) => {
    if (!usecase || usecase.task_type !== TaskType.classification) return [];
    const manual = Array.isArray(usecase.manually_added_labels) ? usecase.manually_added_labels : [];
    return manual.length ? [...manual] : Object.keys(usecase.label_names_count || {});
};

/**
 * The "Optimize on" label a flow is about to use when it is NOT one of the
 * task's labels, else `null`. `null` / empty `label` means "All labels" and
 * is always fine. Both the Data Collection "Next" and the Train "Let's Train"
 * gates block on a non-null result instead of silently falling back to "All
 * labels" (which is what the backend would do).
 */
export const missingOptimizeOnLabel = (label, taskLabels = []) => {
    if (!label) return null;
    const known = Array.isArray(taskLabels) ? taskLabels : [];
    return known.includes(label) ? null : label;
};

/**
 * The label a persisted `label_priority` choice optimizes on: the chosen
 * label for the one-sided kinds, `null` for "equally important" / no choice.
 */
export const labelPriorityOptimizeOnLabel = (labelPriority) => {
    if (!labelPriority || labelPriority.kind === LABEL_PRIORITY_KIND.equal) return null;
    return labelPriority.label || null;
};

const quoteList = (labels = []) => labels.map((l) => `"${l}"`).join(', ');

/**
 * Copy for the blocking "label to optimize on isn't in this task" modal.
 * `surface` is `'data-collection'` (Business Goals choice vs. the labels
 * edited in Data Collection) or `'train'` (the Train step's own
 * "Optimize on" pick).
 */
export const optimizeOnLabelMissingCopy = ({ label, taskLabels = [], surface }) => {
    const title = "Label to optimize on isn't in this task";
    const labelsText = taskLabels.length ? quoteList(taskLabels) : 'no labels yet';
    if (surface === 'train') {
        return {
            title,
            body:
                `"Optimize on" is set to "${label}", which isn't one of this task's labels ` +
                `(${labelsText}). Choose a current label or "All labels" under Advanced ` +
                'settings, then try again.',
        };
    }
    return {
        title,
        body:
            `In Business Goals you chose to optimize on "${label}", but the task labels are ` +
            `now ${labelsText}. Go back to Business Goals and update "What matters most for ` +
            `these labels?", or add "${label}" back to the task labels, before continuing.`,
    };
};

/**
 * Train step defaults seeded from a persisted choice. `null` when the task
 * has no choice or the choice no longer matches its eligible labels (the user
 * then gets the regular defaults: F score, beta 1, all labels).
 *
 * @returns {{ metricOptimization: string, betaForFScore: number, rareClassLabel: string | null } | null}
 */
export const trainingDefaultsFromLabelPriority = (labelPriority, eligibleLabels = []) => {
    if (!labelPriority || !(labelPriority.kind in BETA_BY_KIND)) return null;
    if (labelPriority.kind === LABEL_PRIORITY_KIND.equal) {
        return {
            metricOptimization: LABEL_PRIORITY_METRIC,
            betaForFScore: BETA_BY_KIND[LABEL_PRIORITY_KIND.equal],
            rareClassLabel: null,
        };
    }
    if (!labelPriority.label || !eligibleLabels.includes(labelPriority.label)) return null;
    return {
        metricOptimization: LABEL_PRIORITY_METRIC,
        betaForFScore: BETA_BY_KIND[labelPriority.kind],
        rareClassLabel: labelPriority.label,
    };
};
